import {
  type ReactElement,
  useEffect,
  useState,
  useContext,
  createContext,
} from 'react';

import { type State, type City, getStates } from '../../services/StatesService';
import Search from './Search';
import { useDentistStoreState } from '../../stores/usePaginação';
import {
  useSelectedDentistDataStore,
  useTotalClinicDataStore,
} from '../../stores/usePaginacao';

export interface Specialty {
  idEspecialidade: number;
  nomeEspecialidade: string;
  quantidade: number;
}

interface Plan {
  id: number;
  name: string;
}

interface Bairro {
  Id: number | string;
  Nome: string;
  Sigla?: string;
  Descricao?: string;
}

export interface Dentist {
  clinicaId?: number | string;
  endereco: string;
  bairro: string;
  cep: string;
  cidade: string;
  complemento?: string;
  especialidade: string;
  especialidades?: string;
  idEspecialidade: string;
  idFuncionario: number | string;
  idPlano: null;
  latitude: string;
  logradouro: string;
  longitude: string;
  municipio?: string;
  nomeFuncionario: string;
  nomeFantasia?: string;
  numero?: number | string;
  telefone: any;
  tipoDeLogradouro: 'Rua';
  uf?: string;
  ufSigla: 'PERNAMBUCO';
  tipoEstabelecimento?: string;
  qualiss?: string;
  produtos?: string;
  dentistas?: Array<{
    id: number | string;
    nome: string;
    especialidades: string;
    qualiss?: any[];
  }>;
}

interface DentistsContext {
  states: State[];
  specialties: Specialty[];
  specialty: Specialty;
  city: City;
  state: State;
  bairros: Bairro[];
  bairro: string;
  cep: string;
  resolvedAddress: string;
  plan: Plan;
  plans: Plan[];
  loading: boolean;
  found: boolean;
  docList: Dentist[];
  canSearch: boolean;
  handleSearch: () => Promise<void>;
  handleRefresh: () => Promise<void>;
  lookupCep: (value: string) => Promise<void>;
  setSpecialty: React.Dispatch<React.SetStateAction<Specialty>>;
  setState: React.Dispatch<React.SetStateAction<State>>;
  setCity: React.Dispatch<React.SetStateAction<City>>;
  setBairro: React.Dispatch<React.SetStateAction<string>>;
  setCep: React.Dispatch<React.SetStateAction<string>>;
  setPlan: React.Dispatch<React.SetStateAction<Plan>>;
  reset: () => void;
  editSearch: () => void;
  mapCenter: { lat: number; lng: number };
  setMapCenter: React.Dispatch<
    React.SetStateAction<{ lat: number; lng: number }>
  >;
  selectedDentist: Dentist | null;
  setSelectedDentist: React.Dispatch<React.SetStateAction<Dentist | null>>;
}

const DentistsContext = createContext({} as DentistsContext);

export const useDentistsContext = (): DentistsContext =>
  useContext(DentistsContext);

type DentistSearchVariant = 'embedded' | 'standalone' | 'anafe';

interface DentistSearchProps {
  variant?: DentistSearchVariant;
  includeAllSpecialties?: boolean;
}

const s4eBaseUrl = process.env.REACT_APP_URL_BASE;
const s4eTokenV2 = process.env.REACT_APP_TOKEN_S4E_V2;

const normalizeCoordinate = (value: string | number | null | undefined) =>
  String(value ?? '').replace(',', '.');

const toUpperText = (value: any) =>
  typeof value === 'string' ? value.toLocaleUpperCase('pt-BR') : value;

const normalizeClinic = (clinic: any): Dentist => ({
  ...clinic,
  nomeFantasia: toUpperText(clinic.nomeFantasia),
  razaoSocial: toUpperText(clinic.razaoSocial),
  endereco: toUpperText(clinic.endereco),
  bairro: toUpperText(clinic.bairro),
  cep: toUpperText(clinic.cep),
  cidade: toUpperText(clinic.cidade),
  complemento: toUpperText(clinic.complemento),
  especialidade: toUpperText(clinic.especialidade),
  especialidades: toUpperText(clinic.especialidades),
  logradouro: toUpperText(clinic.logradouro),
  municipio: toUpperText(clinic.municipio),
  nomeFuncionario: toUpperText(clinic.nomeFuncionario),
  tipoEstabelecimento: toUpperText(clinic.tipoEstabelecimento),
  uf: toUpperText(clinic.uf),
  ufSigla: toUpperText(clinic.ufSigla),
  dentistas: Array.isArray(clinic.dentistas)
    ? clinic.dentistas.map(dentist => ({
        ...dentist,
        nome: toUpperText(dentist.nome),
        especialidades: toUpperText(dentist.especialidades),
        qualiss: Array.isArray(dentist.qualiss)
          ? dentist.qualiss.map(qual => ({
              ...qual,
              nomeEspecialidade: toUpperText(qual.nomeEspecialidade),
              Descricao: toUpperText(qual.Descricao),
            }))
          : dentist.qualiss,
      }))
    : clinic.dentistas,
  latitude: normalizeCoordinate(clinic.latitude),
  longitude: normalizeCoordinate(clinic.longitude),
});

export default function DentistSearch({
  variant = 'embedded',
  includeAllSpecialties = false,
}: DentistSearchProps): ReactElement {
  const [states, setStates] = useState<State[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [specialty, setSpecialty] = useState<Specialty>(null);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<State>(null);
  const [city, setCity] = useState<City>(null);
  const [bairros, setBairros] = useState<Bairro[]>([]);
  const [bairro, setBairro] = useState('');
  const [cep, setCep] = useState('');
  const [resolvedAddress, setResolvedAddress] = useState('');
  const [plan, setPlan] = useState<Plan>(null);
  const [found, setFound] = useState(false);
  const docList = useDentistStoreState(x => x.data);
  const setDocList = useDentistStoreState(x => x.setDocList);
  const setTotal = useTotalClinicDataStore(x => x.setTotalClinicData);
  const setSelectedDentistData = useSelectedDentistDataStore(
    x => x.setselectedDentistData,
  );
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(
    null,
  );
  const [selectedDentist, setSelectedDentist] = useState<Dentist | null>(null);

  useEffect(() => {
    getStates().then(states =>
      setStates(
        states
          .filter(s => s.Id !== 0)
          .map(state => ({
            ...state,
            Sigla: toUpperText(state.Sigla),
            Descricao: toUpperText(state.Descricao),
          })),
      ),
    );

    const getRequest = async url => {
      const resp = await fetch(url).then(resp => resp.json());
      return resp.dataObject.dados;
    };

    getRequest(
      `${process.env.REACT_APP_GET_DENTISTS_SPECIALTY}?token=${process.env.REACT_APP_TOKEN_S4E}&sigla=df`,
    ).then(s => {
      const loadedSpecialties = s
        .filter(s => s.idEspecialidade !== 47)
        .map(specialty => ({
          ...specialty,
          nomeEspecialidade: toUpperText(specialty.nomeEspecialidade),
        }));
      setSpecialties(
        includeAllSpecialties
          ? [
              {
                idEspecialidade: 0,
                nomeEspecialidade: 'TODAS',
                quantidade: 0,
              },
              ...loadedSpecialties,
            ]
          : loadedSpecialties,
      );
    });

    setPlans([
      { id: 1, name: 'OdontoClinico' },
      { id: 2, name: 'OdontoDoc' },
      { id: 3, name: 'OdontoOrto' },
      { id: 4, name: 'OdontoProtese' },
      { id: 5, name: 'OdontoKids' },
    ]);
  }, [includeAllSpecialties]);

  useEffect(() => {
    if (!city?.Id || !s4eBaseUrl || !s4eTokenV2) {
      setBairros([]);
      return;
    }

    fetch(
      `${s4eBaseUrl}/api/redeatendimento/Bairros?token=${s4eTokenV2}&municipioId=${city.Id}&apenasRede=1`,
    )
      .then(response => response.json())
      .then(response =>
        setBairros(
          (response.dados || []).map(bairro => ({
            ...bairro,
            Nome: toUpperText(bairro.Nome),
            Descricao: toUpperText(bairro.Descricao),
            Sigla: toUpperText(bairro.Sigla),
          })),
        ),
      )
      .catch(() => setBairros([]));
  }, [city]);

  async function lookupCep(value: string) {
    const onlyDigits = value.replace(/\D/g, '');
    setCep(value);

    if (onlyDigits.length !== 8 || !s4eBaseUrl || !s4eTokenV2) {
      setResolvedAddress('');
      return;
    }

    const response = await fetch(
      `${s4eBaseUrl}/api/redeatendimento/Endereco?token=${s4eTokenV2}&cep=${onlyDigits}`,
      { method: 'POST' },
    );
    const data = await response.json();
    const address = data.dados;

    if (!address?.IdUf || !address?.IdMunicipio) {
      setResolvedAddress('');
      return;
    }

    const selectedState =
      states.find(s => String(s.Id) === String(address.IdUf)) || null;
    const cityData = await fetch(
      `${process.env.REACT_APP_GET_CITIES}?token=${process.env.REACT_APP_TOKEN_S4E}&ufID=${address.IdUf}&apenasRedeCredenciada=1`,
    ).then(response => response.json());
    const cityList = cityData.dados || [];
    const selectedCity =
      cityList.find(c => String(c.Id) === String(address.IdMunicipio)) || null;

    setState(
      selectedState
        ? {
            ...selectedState,
            Sigla: toUpperText(selectedState.Sigla),
            Descricao: toUpperText(selectedState.Descricao),
          }
        : selectedState,
    );
    setCity(
      selectedCity
        ? {
            ...selectedCity,
            Sigla: toUpperText(selectedCity.Sigla),
            Descricao: toUpperText(selectedCity.Descricao),
          }
        : selectedCity,
    );
    setBairro(address.IdBairro ? String(address.IdBairro) : '');
    setResolvedAddress(
      [
        address.Logradouro,
        address.Bairro,
        address.Municipio && `${address.Municipio} - ${address.Uf}`,
      ]
        .filter(Boolean)
        .join(', ')
        .toLocaleUpperCase('pt-BR'),
    );
  }

  async function fetchDentists(clearResults = false) {
    setFound(true);
    setLoading(true);

    if (clearResults) {
      setDocList([]);
      setTotal(0);
      setSelectedDentist(null);
      setSelectedDentistData(null as any);
    }

    try {
      const query = new URLSearchParams({
        ufId: String(state.Id),
        municipioId: String(city.Id),
        especialidadeId: String(specialty.idEspecialidade),
        bairro: bairro || '',
        quantidade: '5',
      });
      const data = await fetch(
        `${
          process.env.REACT_APP_OG_API_URL
        }/info/RedeVendas?${query.toString()}`,
      ).then(resp => resp.json());
      const clinics = Array.isArray(data.clinicas) ? data.clinicas : [];
      const normalizedClinics = clinics
        .filter(clinic => clinic.longitude && clinic.latitude)
        .map(normalizeClinic);

      setDocList(normalizedClinics);
      setTotal(data.total || normalizedClinics.length || 0);
    } catch (err) {
      setDocList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch() {
    await fetchDentists(true);
  }

  async function handleRefresh() {
    if (!state || !city || !specialty) return;
    await fetchDentists();
  }

  const reset = () => {
    setFound(false);
    setDocList([]);
    setCity(null);
    setState(null);
    setSpecialty(null);
    setBairro('');
    setBairros([]);
    setCep('');
    setResolvedAddress('');
    setPlan(null);
    setLoading(false);
    setTotal(0);
    setSelectedDentist(null);
    setSelectedDentistData(null as any);
  };

  const editSearch = () => {
    setFound(false);
  };

  const canSearch = !!specialty && !!state && !!city && !loading;

  return (
    <DentistsContext.Provider
      value={{
        states,
        docList,
        plan,
        specialties,
        found,
        plans,
        specialty,
        loading,
        state,
        city,
        bairros,
        bairro,
        cep,
        resolvedAddress,
        canSearch,
        setSpecialty,
        handleSearch,
        handleRefresh,
        setState,
        setPlan,
        setCity,
        setBairro,
        setCep,
        lookupCep,
        reset,
        editSearch,
        mapCenter,
        setMapCenter,
        selectedDentist,
        setSelectedDentist,
      }}
    >
      {variant === 'anafe' && (
        <div className="mx-auto mt-6 max-w-7xl px-4">
          <img
            src="/assets/logos/logo_anafe.png"
            alt="Logo Anafe Saude"
            className="mx-auto h-44 w-full object-contain md:h-56"
          />
        </div>
      )}
      {variant !== 'embedded' && (
        <div className="mx-auto mt-6 max-w-7xl px-4">
          <h1 className="text-2xl font-semibold text-primary-main">
            Encontre seu dentista
          </h1>
        </div>
      )}
      <Search />
    </DentistsContext.Provider>
  );
}
